import { SocketClient } from "cbcore-ts"
import {
    IF,
    IS,
    nil,
    NO,
    UIColor,
    UIRoute,
    UITextView,
    UIView,
    UIViewBroadcastEvent,
    UIViewController,
    YES
} from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import { CBButtonGroup } from "../Custom components/CB/CBButtonGroup"
import {
    CBDataViewCellDescriptor,
    CBDataViewCellDescriptorWithObject,
    CBSelectionColumnDescriptor
} from "../Custom components/CB/CBDataView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { RHRadioButton } from "../Custom components/RH/RHRadioButton"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { formatRHDate } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHColors, RHSpacing, RHTables } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHCompanyViewTokens, RHInvoicesViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { RHInvoiceEntry } from "../SocketClientFunctions"
import { InvoicePaymentViewController } from "./InvoicePayment/InvoicePaymentViewController"

const INVOICE_SELECTION_IDENTIFIER = "companyInvoices"


// ─── Helpers ─────────────────────────────────────────────────────────────────

function _invoiceStatusInfo(status: RHInvoiceEntry["status"]): { text: string; color: UIColor } {
    if (status === "pending") {
        return { text: "Ootel", color: RHColor.warning }
    }
    if (status === "overdue") {
        return { text: "Tähtaeg ületatud", color: RHColor.danger }
    }
    if (status === "paid") {
        return { text: "Makstud", color: RHColor.success }
    }
    return { text: "Tühistatud", color: RHColor.textMuted }
}

function _invoiceDescriptors(): (CBDataViewCellDescriptor | CBSelectionColumnDescriptor)[] {
    return [
        {
            type: "selection",
            identifier: INVOICE_SELECTION_IDENTIFIER,
            enabled: (invoice: RHInvoiceEntry) =>
                invoice.status === "pending" || invoice.status === "overdue",
            selectionMode: "multiple",
            masterSelectionEnabled: YES
        },
        CBDataViewCellDescriptorWithObject<RHInvoiceEntry>({
            title: "Kirjeldus",
            keyPath: "description",
            allowSorting: YES,
            titleLabelAlignment: UITextView.textAlignment.left,
            cellLabelAlignment: UITextView.textAlignment.left,
            updateCell: (cellView, dataItem) => {
                cellView.titleLabel!.text = IF(dataItem.description)(() =>
                    RHTextSanitizer.escapedHTML(dataItem.description!)
                ).ELSE(() => "-")
            }
        }),
        CBDataViewCellDescriptorWithObject<RHInvoiceEntry>({
            title: "Summa",
            keyPath: "amount",
            allowSorting: YES,
            dataType: "number",
            titleLabelAlignment: UITextView.textAlignment.center,
            cellLabelAlignment: UITextView.textAlignment.center,
            updateCell: (cellView, dataItem) => {
                cellView.titleLabel!.text = RHEuroFormatter.formattedCurrency(dataItem.amount, {
                    currency: dataItem.currency ?? "EUR",
                    minimumFractionDigits: 2
                })
            }
        }),
        CBDataViewCellDescriptorWithObject<RHInvoiceEntry>({
            title: "Väljastatud",
            keyPath: "issuedAt",
            allowSorting: YES,
            dataType: "string",
            titleLabelAlignment: UITextView.textAlignment.center,
            cellLabelAlignment: UITextView.textAlignment.center,
            updateCell: (cellView, dataItem) => {
                if (!dataItem.issuedAt) {
                    cellView.titleLabel!.text = "-"
                    return
                }
                cellView.titleLabel!.text = formatRHDate(new Date(dataItem.issuedAt))
            }
        }),
        CBDataViewCellDescriptorWithObject<RHInvoiceEntry>({
            title: "Maksetähtaeg",
            keyPath: "dueDate",
            allowSorting: YES,
            dataType: "string",
            titleLabelAlignment: UITextView.textAlignment.center,
            cellLabelAlignment: UITextView.textAlignment.center,
            updateCell: (cellView, dataItem) => {
                if (!dataItem.dueDate) {
                    cellView.titleLabel!.text = "-"
                    return
                }
                cellView.titleLabel!.text = formatRHDate(new Date(dataItem.dueDate))
                if (dataItem.status === "overdue") {
                    cellView.titleLabel!.textColor = RHColor.danger
                    return
                }
                cellView.titleLabel!.textColor = RHColor.textBlack
            }
        }),
        CBDataViewCellDescriptorWithObject<RHInvoiceEntry>({
            title: "Staatus",
            keyPath: "status",
            allowSorting: YES,
            titleLabelAlignment: UITextView.textAlignment.center,
            cellLabelAlignment: UITextView.textAlignment.center,
            updateCell: (cellView, dataItem) => {
                const info = _invoiceStatusInfo(dataItem.status)
                cellView.titleLabel!.text = info.text
                cellView.titleLabel!.textColor = info.color
            }
        }),
        CBDataViewCellDescriptorWithObject<RHInvoiceEntry, RHTableActionButtonView>({
            title: "Vaata",
            keyPath: "_id",
            allowSorting: NO,
            titleLabelAlignment: UITextView.textAlignment.center,
            cellLabelAlignment: UITextView.textAlignment.center,
            initCustomView: () => new RHTableActionButtonView(),
            updateCell: (cellView, dataItem) => {
                cellView.customView.text = "Vaata"
                cellView.customView.buttonWasPressed = () => {
                    UIRoute.currentRoute
                        .routeWithViewControllerComponent(InvoicePaymentViewController, {
                            invoiceIds: dataItem._id
                        })
                        .apply()
                }
            }
        })
    ]
}


// ─── InvoicesViewController ───────────────────────────────────────────────────

export class InvoicesViewController extends UIViewController {
    
    static override readonly routeComponentName = "invoices"
    static override readonly ParameterIdentifierName = {
        tab: "tab"
    }
    
    // ── Title ──────────────────────────────────────────────────────────────────
    
    readonly titleLabel = new UITextView(
        undefined,
        UITextView.type.header4
    ).configuredWithObject({
        text: "Arved",
        backgroundColor: UIColor.transparentColor,
        textColor: RHColors.textPrimary
    }).addedAsSubviewToView(this.view)
    
    // ── Tabs ───────────────────────────────────────────────────────────────────
    
    readonly personalTabButton = new RHRadioButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Minu arved" }
    })
    
    // Detached until we confirm the user is a company administrator
    readonly companyTabButton = new RHRadioButton().configuredWithObject({
        titleLabel: { text: "Ettevõtte arved" }
    })
    
    readonly tabGroup = new CBButtonGroup({
        "personal": this.personalTabButton,
        "company": this.companyTabButton
    }).configuredWithObject({
        value: "personal",
        selectionDidChange: (_button, value) => this._tabDidChange(value ?? "personal")
    })
    
    // ── Pay button (company tab only) ──────────────────────────────────────────
    
    // Detached until the company tab is active and selection is non-empty.
    readonly paySelectedInvoicesButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Maksa valitud arved" },
        variant: "primary"
    })
    
    // ── Data views ─────────────────────────────────────────────────────────────
    
    readonly personalDataView = new RHDataView<RHInvoiceEntry>().configuredWithObject({
        isSearchAvailable: NO,
        noDataMessage: "Isiklikke arveid ei leitud.",
        descriptors: [
            CBDataViewCellDescriptorWithObject<RHInvoiceEntry>({
                title: "Kirjeldus",
                keyPath: "description",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.titleLabel!.text = IF(dataItem.description)(() =>
                    RHTextSanitizer.escapedHTML(dataItem.description!)
                ).ELSE(() => "-")
                }
            }),
            CBDataViewCellDescriptorWithObject<RHInvoiceEntry>({
                title: "Summa",
                keyPath: "amount",
                allowSorting: YES,
                dataType: "number",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    cellView.titleLabel!.text = RHEuroFormatter.formattedCurrency(dataItem.amount, {
                        currency: dataItem.currency ?? "EUR",
                        minimumFractionDigits: 2
                    })
                }
            }),
            CBDataViewCellDescriptorWithObject<RHInvoiceEntry>({
                title: "Väljastatud",
                keyPath: "issuedAt",
                allowSorting: YES,
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (!dataItem.issuedAt) {
                        cellView.titleLabel!.text = "-"
                        return
                    }
                    cellView.titleLabel!.text = formatRHDate(new Date(dataItem.issuedAt))
                }
            }),
            CBDataViewCellDescriptorWithObject<RHInvoiceEntry>({
                title: "Maksetähtaeg",
                keyPath: "dueDate",
                allowSorting: YES,
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (!dataItem.dueDate) {
                        cellView.titleLabel!.text = "-"
                        return
                    }
                    cellView.titleLabel!.text = formatRHDate(new Date(dataItem.dueDate))
                }
            }),
            CBDataViewCellDescriptorWithObject<RHInvoiceEntry>({
                title: "Staatus",
                keyPath: "status",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    const info = _invoiceStatusInfo(dataItem.status)
                    cellView.titleLabel!.text = info.text
                    cellView.titleLabel!.textColor = info.color
                }
            }),
            CBDataViewCellDescriptorWithObject<RHInvoiceEntry, RHTableActionButtonView>({
                title: "Vaata",
                keyPath: "_id",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new RHTableActionButtonView(),
                updateCell: (cellView, dataItem) => {
                    cellView.customView.text = "Vaata"
                    cellView.customView.buttonWasPressed = () => {
                        UIRoute.currentRoute
                            .routeWithViewControllerComponent(InvoicePaymentViewController, {
                                invoiceIds: dataItem._id
                            })
                            .apply()
                    }
                }
            })
        ]
    })
    
    readonly companyDataView = new RHDataView<RHInvoiceEntry>().configuredWithObject({
        isSearchAvailable: NO,
        noDataMessage: "Ettevõtte arveid ei leitud.",
        descriptors: _invoiceDescriptors(),
        selectionDidChange: (_identifier: string) => this._updatePayButton()
    })
    
    // ── State ──────────────────────────────────────────────────────────────────
    
    private _isAdministrator = NO
    private _activeDataView: RHDataView<RHInvoiceEntry> = this.personalDataView
    private _suppressTabChangeCallback = NO
    
    // ── Constructor ────────────────────────────────────────────────────────────
    
    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({ backgroundColor: RHColor.background })
        this.personalDataView.addedAsSubviewToView(this.view)

        this._applyTableColumnLayout(this.personalDataView, [2.6, 1, 1.1, 1.1, 1, 0.6])
        this._applyTableColumnLayout(this.companyDataView, [0.6, 2.6, 1, 1.1, 1.1, 1, 0.6])

        // noinspection JSConstantReassignment
        this.paySelectedInvoicesButton.controlEventTargetAccumulator.PrimaryActionTriggered =
            () => this._handlePaySelectedInvoices()
    }

    // ── Table column layout ──────────────────────────────────────────────────────

    private _applyTableColumnLayout(dataView: RHDataView<RHInvoiceEntry>, columnWeights: number[]) {
        dataView.tableHeaderView.configureWithObject({
            cellWeights: columnWeights,
            intrinsicContentHeight: () => 64
        })
        dataView.tableView.configureWithObject({
            cellWeights: columnWeights
        })
        this._configureTableHeaderCells(dataView)
        dataView.tableHeaderView.setNeedsLayout()
        dataView.tableView.setNeedsLayout()
    }

    private _configureTableHeaderCells(dataView: RHDataView<RHInvoiceEntry>) {
        for (const headerCell of dataView.tableHeaderView.cells) {
            const buttonView = headerCell.buttonView
            if (buttonView) {
                buttonView.contentPadding = 2
            }
            const titleLabel = buttonView?.titleLabel ?? headerCell.titleLabel
            if (!titleLabel) {
                continue
            }
            titleLabel.isSingleLine = NO
            titleLabel.style.whiteSpace = "normal"
            titleLabel.style.lineHeight = RHInvoicesViewTokens.multilineHeaderLineHeight
            titleLabel.textElementView.style.whiteSpace = "normal"
            titleLabel.textElementView.style.lineHeight = RHInvoicesViewTokens.multilineHeaderLineHeight
        }
    }
    
    // ── Lifecycle ──────────────────────────────────────────────────────────────
    
    override async viewDidAppear() {
        await super.viewDidAppear()
        await this._loadPermissionsAndUpdateTabs()
        await this._loadActiveTab()
    }

    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (
            event.name === RHCore.broadcastEventName.activeCompanyDidChange ||
            event.name === RHCore.broadcastEventName.currentCompanyAffiliationDidChange
        ) {
            // Administrator status and the "Ettevõtte arved" tab are scoped to
            // whichever company is active, and companyDataView holds invoices
            // fetched for the previously active company — reload both rather
            // than leaving a stale list (and a possibly stale Pay button) on
            // screen after a company switch.
            this._loadPermissionsAndUpdateTabs().then(() =>
                this._loadActiveTab()
            ).catch(error => console.error(
                "[InvoicesViewController] Failed to reload after company change:", error
            ))
        }
    }

    // ── Permission + tab visibility ────────────────────────────────────────────
    
    private async _loadPermissionsAndUpdateTabs() {
        try {
            const affiliation = await RHCore.sharedInstance.currentCompanyAffiliation
            this._isAdministrator = IS(affiliation) && affiliation.isAdministrator
            
            if (this._isAdministrator) {
                if (!this.companyTabButton.superview) {
                    this.companyTabButton.addedAsSubviewToView(this.view)
                }
            }
            else {
                this.companyTabButton.removeFromSuperview()
                if (this.tabGroup.value === "company") {
                    this.tabGroup.value = "personal"
                    this._activeDataView = this.personalDataView
                }
            }
        }
        catch (error) {
            console.error("[InvoicesViewController] Failed to load permissions:", error)
        }
        this.view.setNeedsLayoutUpToRootView()
    }
    
    // ── Tab switching ──────────────────────────────────────────────────────────
    
    private _tabDidChange(value: string) {
        if (this._suppressTabChangeCallback) {
            return
        }
        
        this._activeDataView.removeFromSuperview()
        
        if (value === "company") {
            this._activeDataView = this.companyDataView
        }
        else {
            this._activeDataView = this.personalDataView
        }
        
        UIRoute.currentRoute
            .routeBySettingParameterInComponent(
                InvoicesViewController.routeComponentName,
                InvoicesViewController.ParameterIdentifierName.tab,
                value
            )
            .applyByReplacingCurrentRouteInHistory()
        
        this._activeDataView.addedAsSubviewToView(this.view)
        this._updatePayButton()
        this.view.setNeedsLayoutUpToRootView()
        this._loadActiveTab().catch(e => console.error(e))
    }
    
    // ── Pay button visibility ──────────────────────────────────────────────────
    
    private _updatePayButton() {
        const isCompanyTab = this.tabGroup.value === "company"
        const hasSelection = isCompanyTab &&
            this.companyDataView.selectionForIdentifier(INVOICE_SELECTION_IDENTIFIER).length > 0
        
        if (hasSelection && !this.paySelectedInvoicesButton.superview) {
            this.paySelectedInvoicesButton.addedAsSubviewToView(this.view)
        }
        else if (!hasSelection && this.paySelectedInvoicesButton.superview) {
            this.paySelectedInvoicesButton.removeFromSuperview()
        }
        
        this.view.setNeedsLayoutUpToRootView()
    }
    
    // ── Data loading ───────────────────────────────────────────────────────────
    
    private async _loadActiveTab() {
        if (this._activeDataView === this.personalDataView) {
            await this._loadPersonalInvoices()
        }
        else {
            await this._loadCompanyInvoices()
        }
    }
    
    private async _loadPersonalInvoices() {
        try {
            const response = (await SocketClient.GetMyInvoices()).result
            this.personalDataView.data = response.invoices
            this.view.setNeedsLayout()
        }
        catch (error) {
            console.error("[InvoicesViewController] Failed to load personal invoices:", error)
        }
    }
    
    private async _loadCompanyInvoices() {
        try {
            const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
            const response = (await SocketClient.GetCompanyInvoices({ companyId })).result
            this.companyDataView.data = response.invoices
            this.view.setNeedsLayout()
        }
        catch (error) {
            console.error("[InvoicesViewController] Failed to load company invoices:", error)
        }
    }
    
    // ── Pay selected invoices ──────────────────────────────────────────────────
    
    private _handlePaySelectedInvoices() {
        const selectedItems = this.companyDataView.selectionForIdentifier(INVOICE_SELECTION_IDENTIFIER)
        if (selectedItems.length === 0) {
            return
        }
        const invoiceIds = selectedItems.map(item => item.dataItem._id).join(",")
        UIRoute.currentRoute
            .routeWithViewControllerComponent(InvoicePaymentViewController, {
                invoiceIds: invoiceIds
            })
            .apply()
    }
    
    // ── Route ──────────────────────────────────────────────────────────────────
    
    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
        const component = route.componentWithViewController(InvoicesViewController)
        const tabParam = component?.parameters[InvoicesViewController.ParameterIdentifierName.tab]
        if (tabParam && tabParam !== this.tabGroup.value) {
            // Set the group value without triggering _tabDidChange via selectionDidChange,
            // then sync state ourselves.
            this._suppressTabChangeCallback = YES
            this.tabGroup.value = tabParam
            this._suppressTabChangeCallback = NO
            this._syncTabState(tabParam)
        }
    }
    
    // Syncs _activeDataView and reloads data for the given tab value
    // without writing back to the route (used when route drives the tab).
    private _syncTabState(value: string) {
        this._activeDataView.removeFromSuperview()
        
        if (value === "company") {
            this._activeDataView = this.companyDataView
        }
        else {
            this._activeDataView = this.personalDataView
        }
        
        this._activeDataView.addedAsSubviewToView(this.view)
        this._updatePayButton()
        this.view.setNeedsLayoutUpToRootView()
        this._loadActiveTab().catch(e => console.error(e))
    }
    
    // ── Layout ─────────────────────────────────────────────────────────────────
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const bounds = this.view.contentBoundsWithInset(padding)
        const hasMultipleTabs = IS(this.companyTabButton.superview)
        const hasPayButton = IS(this.paySelectedInvoicesButton.superview)
        
        const personalTabWidth = this.personalTabButton.titleLabel.intrinsicContentWidth(
            RHSpacing.xLarge
        ) + RHCompanyViewTokens.tabButtonWidthSafetyBuffer
        
        const companyTabWidth = this.companyTabButton.titleLabel.intrinsicContentWidth(
            RHSpacing.xLarge
        ) + RHCompanyViewTokens.tabButtonWidthSafetyBuffer
        
        const titleFrame = bounds.rectangleWithHeight(labelHeight)
        titleFrame.assignedAsFrameOfView(this.titleLabel)
        
        const tabBarFrame = titleFrame.rectangleForNextRow(
            padding,
            RHSpacing.xLarge
        )
        
        if (hasMultipleTabs) {
            const tabGroupWidth = personalTabWidth
                + RHSpacing.xLarge
                + companyTabWidth
            
            if (hasPayButton) {
                // Tabs left-aligned, pay button right-aligned in the same row.
                tabBarFrame
                    .rectangleWithWidth(tabGroupWidth, 0)
                    .distributeViewsAlongWidth(
                        [this.personalTabButton, this.companyTabButton],
                        [nil, nil],
                        RHSpacing.xLarge,
                        [personalTabWidth, companyTabWidth]
                    )
                this.paySelectedInvoicesButton.frame = tabBarFrame
                    .rectangleWithWidth(padding * 12, 1)
                    .rectangleWithHeight(RHSpacing.xLarge, 0.5)
            }
            else {
                tabBarFrame
                    .rectangleWithWidth(tabGroupWidth, 0.5)
                    .distributeViewsAlongWidth(
                        [this.personalTabButton, this.companyTabButton],
                        [nil, nil],
                        RHSpacing.xLarge,
                        [personalTabWidth, companyTabWidth]
                    )
            }
        }
        else {
            tabBarFrame
                .rectangleWithWidth(personalTabWidth, 0)
                .assignedAsFrameOfView(this.personalTabButton)
        }
        
        tabBarFrame
            .rectangleForNextRow(RHSpacing.medium, RHTables.dataViewPageHeight)
            .assignedAsFrameOfView(this._activeDataView)
    }
    
}

